// Kontrast enligt WCAG 2.1 för byråns accentfärg (ADR 0054 och 0057). Samma
// regler i widgeten, offerten och byråns inställningar. Filen importerar
// ingenting, så att widgeten kan använda den utan zod
// (`@leadledger/domain/contrast`).

/** Brödtextens färg i designen. */
export const INK = '#15201B';
export const WHITE = '#FFFFFF';

/** Kravet för vanlig text i WCAG 2.1 AA. */
export const AA_TEXT = 4.5;

/** En färg som #RRGGBB, eller undefined. */
export function parseHex(hex: string): [number, number, number] | undefined {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  return m
    ? [parseInt(m[1] ?? '', 16), parseInt(m[2] ?? '', 16), parseInt(m[3] ?? '', 16)]
    : undefined;
}

/** Relativ luminans enligt WCAG 2.1. */
function luminans(hex: string): number {
  const [r, g, b] = (parseHex(hex) ?? [0, 0, 0]).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Kontrasten mellan två färger enligt WCAG 2.1, från 1 till 21. */
export function contrast(a: string, b: string): number {
  const [ljus, mork] = [luminans(a), luminans(b)].sort((x, y) => y - x) as [number, number];
  return (ljus + 0.05) / (mork + 0.05);
}

/** Färgen blandad med svart, till exempel 0.2 för 20 % mörkare. */
function morkare(hex: string, andel: number): string {
  const [r, g, b] = parseHex(hex) ?? [0, 0, 0];
  const kanal = (v: number) =>
    Math.round(v * (1 - andel))
      .toString(16)
      .padStart(2, '0');
  return `#${kanal(r)}${kanal(g)}${kanal(b)}`.toUpperCase();
}

/**
 * Accenten som text, ramar och markeringar på vitt, till exempel i länkar. En
 * ljus accent görs mörkare tills den klarar AA, så att byråns färgton finns kvar.
 */
export function accentText(accent: string): string {
  for (let steg = 0; steg <= 20; steg++) {
    const farg = morkare(accent, steg / 20);
    if (contrast(farg, WHITE) >= AA_TEXT) return farg;
  }
  return INK;
}

/**
 * Knappens bakgrund och text. Vit text om den syns tillräckligt, annars
 * brödtextens färg, och om ingen av dem räcker en mörkare accent med vit text.
 */
export function accentFill(accent: string): { fill: string; on: string } {
  if (contrast(WHITE, accent) >= AA_TEXT) return { fill: accent, on: WHITE };
  if (contrast(INK, accent) >= AA_TEXT) return { fill: accent, on: INK };
  return { fill: accentText(accent), on: WHITE };
}

/** En ljus ton av accenten, som bakgrund. */
export function accentTint(accent: string, alpha = 0.08): string {
  const [r, g, b] = parseHex(accent) ?? [35, 64, 107];
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Hur byråns accent klarar kontrasten, och vilka färger som används i stället. */
export interface AccentContrast {
  /** Kontrasten mot vitt, avrundad till två decimaler. */
  ratio: number;
  /** Om accenten klarar AA som text på vitt utan att ändras. */
  passesAA: boolean;
  /** Färgen för text och länkar på vitt. */
  text: string;
  /** Knappens bakgrund och textfärg. */
  fill: string;
  onFill: string;
}

export function accentContrast(accent: string): AccentContrast {
  const ratio = Math.round(contrast(accent, WHITE) * 100) / 100;
  const { fill, on } = accentFill(accent);
  return {
    ratio,
    passesAA: contrast(accent, WHITE) >= AA_TEXT,
    text: accentText(accent),
    fill,
    onFill: on,
  };
}
